// API Service for Netlen Webmail
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}/api${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Bir hata oluştu');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    
    return data;
  }

  async verify2FA(email, code, sessionToken) {
    const data = await this.request('/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ email, code, session_token: sessionToken }),
    });
    
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    
    return data;
  }

  async tokenLogin(token) {
    const data = await this.request(`/auth/token/${token}`, {
      method: 'GET',
    });
    
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    
    return data;
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } catch (e) {
      // Ignore errors
    }
    this.setToken(null);
  }

  // Mail
  async getFolders() {
    return this.request('/mail/folders');
  }

  async getQuota() {
    return this.request('/mail/quota');
  }

  async getMessages(folder = 'INBOX', page = 1, perPage = 50, search = null, label = null) {
    let url = `/mail/messages?folder=${encodeURIComponent(folder)}&page=${page}&per_page=${perPage}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (label) {
      url += `&label=${label}`;
    }
    return this.request(url);
  }

  async getThread(msgId, folder = 'INBOX') {
    return this.request(`/mail/thread/${msgId}?folder=${encodeURIComponent(folder)}`);
  }

  // Etiketler
  async getLabels() {
    return this.request('/labels');
  }

  async createLabel(name, color) {
    return this.request('/labels', { method: 'POST', body: JSON.stringify({ name, color }) });
  }

  async updateLabel(id, name, color) {
    return this.request(`/labels/${id}`, { method: 'PUT', body: JSON.stringify({ name, color }) });
  }

  async deleteLabel(id) {
    return this.request(`/labels/${id}`, { method: 'DELETE' });
  }

  async applyLabels(folder, ids, add = [], remove = []) {
    return this.request('/labels/apply', { method: 'POST', body: JSON.stringify({ folder, ids, add, remove }) });
  }

  async getMessage(msgId, folder = 'INBOX') {
    return this.request(`/mail/message/${msgId}?folder=${encodeURIComponent(folder)}`);
  }

  // extra: { draft_key, replace_uid } -> gönderim başarılı olunca taslak sunucuda silinir
  async sendEmail(to, subject, bodyHtml, bodyText = null, cc = null, bcc = null, attachments = null, extra = {}) {
    return this.request('/mail/send', {
      method: 'POST',
      body: JSON.stringify({
        to,
        subject,
        body_html: bodyHtml,
        body_text: bodyText,
        cc,
        bcc,
        attachments,
        ...extra,
      }),
    });
  }

  // Taslaklar
  async saveDraft(draft) {
    return this.request('/mail/drafts', { method: 'POST', body: JSON.stringify(draft) });
  }

  async deleteDraft(draftKey, replaceUid = null) {
    const query = replaceUid ? `?replace_uid=${encodeURIComponent(replaceUid)}` : '';
    return this.request(`/mail/drafts/${encodeURIComponent(draftKey)}${query}`, { method: 'DELETE' });
  }

  // Göndermeyi zamanla
  async scheduleEmail(payload) {
    return this.request('/mail/schedule', { method: 'POST', body: JSON.stringify(payload) });
  }

  async getScheduled() {
    return this.request('/mail/scheduled');
  }

  async cancelScheduled(id) {
    return this.request(`/mail/scheduled/${id}`, { method: 'DELETE' });
  }

  // Şablonlar
  async getTemplates() {
    return this.request('/mail/templates');
  }

  async createTemplate(template) {
    return this.request('/mail/templates', { method: 'POST', body: JSON.stringify(template) });
  }

  async deleteTemplate(id) {
    return this.request(`/mail/templates/${id}`, { method: 'DELETE' });
  }

  async toggleStar(msgId, starred = true, folder = 'INBOX') {
    return this.request(`/mail/message/${msgId}/star?starred=${starred}&folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
    });
  }

  async markAsRead(msgId, folder = 'INBOX') {
    return this.request(`/mail/message/${msgId}/read?folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
    });
  }

  async deleteMessage(msgId, folder = 'INBOX') {
    return this.request(`/mail/message/${msgId}?folder=${encodeURIComponent(folder)}`, {
      method: 'DELETE',
    });
  }

  async moveMessage(msgId, fromFolder, toFolder) {
    return this.request(`/mail/message/${msgId}/move?from_folder=${encodeURIComponent(fromFolder)}&to_folder=${encodeURIComponent(toFolder)}`, {
      method: 'POST',
    });
  }

  // Contacts
  async getContacts(search = null, favoritesOnly = false, page = 1, perPage = 50) {
    let url = `/contacts?page=${page}&per_page=${perPage}&favorites_only=${favoritesOnly}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.request(url);
  }

  async getContactSuggestions(query, limit = 5) {
    return this.request(`/contacts/suggestions?query=${encodeURIComponent(query)}&limit=${limit}`);
  }

  async getContact(id) {
    return this.request(`/contacts/${id}`);
  }

  async createContact(contact) {
    return this.request('/contacts', {
      method: 'POST',
      body: JSON.stringify(contact),
    });
  }

  async updateContact(id, contact) {
    return this.request(`/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(contact),
    });
  }

  async deleteContact(id) {
    return this.request(`/contacts/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleContactFavorite(id) {
    return this.request(`/contacts/${id}/favorite`, {
      method: 'POST',
    });
  }

  // Calendar
  async getEvents(startDate = null, endDate = null) {
    let url = '/calendar';
    const params = [];
    if (startDate) params.push(`start_date=${startDate.toISOString()}`);
    if (endDate) params.push(`end_date=${endDate.toISOString()}`);
    if (params.length) url += '?' + params.join('&');
    return this.request(url);
  }

  async getEventsRange(start, end) {
    const params = `start_date=${encodeURIComponent(start.toISOString())}&end_date=${encodeURIComponent(end.toISOString())}`;
    return this.request(`/calendar?${params}`);
  }

  async getEventsByMonth(year, month) {
    return this.request(`/calendar/month/${year}/${month}`);
  }

  async getEventDatesForMonth(year, month) {
    // Returns dates that have events for calendar highlighting
    const data = await this.request(`/calendar/month/${year}/${month}`);
    return data.events?.map(e => e.start_time?.split('T')[0]) || [];
  }

  async getEvent(id) {
    return this.request(`/calendar/${id}`);
  }

  async createEvent(event) {
    return this.request('/calendar', {
      method: 'POST',
      body: JSON.stringify(event),
    });
  }

  async updateEvent(id, event) {
    return this.request(`/calendar/${id}`, {
      method: 'PUT',
      body: JSON.stringify(event),
    });
  }

  async deleteEvent(id) {
    return this.request(`/calendar/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings
  async getSettings() {
    return this.request('/settings');
  }

  async updateSettings(settings) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async updateProfile(displayName, avatarUrl) {
    return this.request('/settings/profile', {
      method: 'PUT',
      body: JSON.stringify({
        display_name: displayName,
        avatar_url: avatarUrl,
      }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/settings/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  }

  // 2FA
  async setup2FA(method, phoneNumber = null) {
    return this.request('/settings/2fa/setup', {
      method: 'POST',
      body: JSON.stringify({ method, phone_number: phoneNumber }),
    });
  }

  async verify2FASetup(code) {
    return this.request('/settings/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disable2FA() {
    return this.request('/settings/2fa', {
      method: 'DELETE',
    });
  }

  async updatePhoneNumber(phoneNumber) {
    return this.request('/settings/phone', {
      method: 'PUT',
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  }

  async verifyPhoneChange(code) {
    return this.request('/settings/phone/verify', {
      method: 'PUT',
      body: JSON.stringify({ code }),
    });
  }

  // IP Restriction
  async enableIPRestriction() {
    return this.request('/settings/ip-restriction/enable', {
      method: 'POST',
    });
  }

  async disableIPRestriction() {
    return this.request('/settings/ip-restriction/disable', {
      method: 'DELETE',
    });
  }

  async addAllowedIP(ipAddress) {
    return this.request('/settings/ip-restriction/add', {
      method: 'POST',
      body: JSON.stringify({ ip_address: ipAddress }),
    });
  }

  async removeAllowedIP(ipAddress) {
    return this.request(`/settings/ip-restriction/${encodeURIComponent(ipAddress)}`, {
      method: 'DELETE',
    });
  }

  // İmzalar
  async getSignatures() {
    return this.request('/signatures');
  }

  async createSignature(signature) {
    return this.request('/signatures', { method: 'POST', body: JSON.stringify(signature) });
  }

  async updateSignature(id, signature) {
    return this.request(`/signatures/${id}`, { method: 'PUT', body: JSON.stringify(signature) });
  }

  async deleteSignature(id) {
    return this.request(`/signatures/${id}`, { method: 'DELETE' });
  }

  // Posta kuralları (cPanel): filtreler, otomatik yanıt, yönlendirme, engellenenler
  async getMailRulesStatus() {
    return this.request('/mailrules/status');
  }

  async getMailFilters() {
    return this.request('/mailrules/filters');
  }

  async createMailFilter(filter) {
    return this.request('/mailrules/filters', { method: 'POST', body: JSON.stringify(filter) });
  }

  async updateMailFilter(name, filter) {
    return this.request(`/mailrules/filters/${encodeURIComponent(name)}`, { method: 'PUT', body: JSON.stringify(filter) });
  }

  async deleteMailFilter(name) {
    return this.request(`/mailrules/filters/${encodeURIComponent(name)}`, { method: 'DELETE' });
  }

  async getAutoreply() {
    return this.request('/mailrules/autoresponder');
  }

  async setAutoreply(config) {
    return this.request('/mailrules/autoresponder', { method: 'PUT', body: JSON.stringify(config) });
  }

  async deleteAutoreply() {
    return this.request('/mailrules/autoresponder', { method: 'DELETE' });
  }

  async getForwarding() {
    return this.request('/mailrules/forwarding');
  }

  async addForwarding(dest, keepCopy = true) {
    return this.request('/mailrules/forwarding', { method: 'POST', body: JSON.stringify({ dest, keep_copy: keepCopy }) });
  }

  async removeForwarding(dest) {
    return this.request(`/mailrules/forwarding?dest=${encodeURIComponent(dest)}`, { method: 'DELETE' });
  }

  async setForwardingKeepCopy(enabled) {
    return this.request('/mailrules/forwarding/keep-copy', { method: 'PUT', body: JSON.stringify({ enabled }) });
  }

  async getBlocked() {
    return this.request('/mailrules/blocked');
  }

  async addBlocked(address) {
    return this.request('/mailrules/blocked', { method: 'POST', body: JSON.stringify({ address }) });
  }

  async removeBlocked(address) {
    return this.request(`/mailrules/blocked?address=${encodeURIComponent(address)}`, { method: 'DELETE' });
  }

  // Bulk Mail (Toplu Mail)
  async getBulkConfig() {
    return this.request('/bulk/config');
  }

  async getBulkCampaigns() {
    return this.request('/bulk/campaigns');
  }

  async getBulkCampaign(id) {
    return this.request(`/bulk/campaigns/${id}`);
  }

  async createBulkCampaign(campaign) {
    return this.request('/bulk/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaign),
    });
  }

  async validateBulkRecipients(recipients) {
    return this.request('/bulk/validate-recipients', {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    });
  }

  async pauseBulkCampaign(id) {
    return this.request(`/bulk/campaigns/${id}/pause`, { method: 'POST' });
  }

  async resumeBulkCampaign(id) {
    return this.request(`/bulk/campaigns/${id}/resume`, { method: 'POST' });
  }

  async cancelBulkCampaign(id) {
    return this.request(`/bulk/campaigns/${id}/cancel`, { method: 'POST' });
  }

  async sendBulkTest(payload) {
    return this.request('/bulk/test', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export const api = new ApiService();
export default api;
